import { Head } from "@inertiajs/react"
import {
  ArrowLeft,
  Link01,
  MarkerPin02,
  PhoneCall01,
  SearchLg,
  Send01,
  VideoRecorder,
} from "@untitledui/icons"
import { Fragment } from "react"

import { HeaderNavigationBase } from "@/components/application/app-navigation/header-navigation"
import { ContentDivider } from "@/components/application/content-divider/content-divider"
import { MessageActionAdvanced } from "@/components/application/messaging/message-action-advanced"
import { MessageActionMinimal } from "@/components/application/messaging/message-action-minimal"
import {
  type Message,
  MessageItem,
} from "@/components/application/messaging/messaging"
import { TableRowActionsDropdown } from "@/components/application/table/table"
import { Avatar } from "@/components/base/avatar/avatar"
import { AvatarProfilePhoto } from "@/components/base/avatar/avatar-profile-photo"
import { VerifiedTick } from "@/components/base/avatar/base-components"
import { Badge } from "@/components/base/badges/badges"
import { Button } from "@/components/base/buttons/button"
import { Checkbox } from "@/components/base/checkbox/checkbox"
import { Input } from "@/components/base/input/input"
import { ProgressBar } from "@/components/base/progress-indicators/progress-indicators"
import {
  RadioButton,
  RadioGroup,
} from "@/components/base/radio-buttons/radio-buttons"
import { Toggle } from "@/components/base/toggle/toggle"

// Helper function for formatting relative time
const formatRelativeTime = (timestamp: number): string => {
  const now = Date.now()
  const diffInMinutes = Math.floor((now - timestamp) / (1000 * 60))
  const diffInHours = Math.floor(diffInMinutes / 60)
  const diffInDays = Math.floor(diffInHours / 24)

  if (diffInMinutes < 1) {
    return "Just now"
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} mins ago`
  } else if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours === 1 ? "" : "s"} ago`
  } else if (diffInDays === 1) {
    // Yesterday - show time
    const date = new Date(timestamp)
    const time = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })

    return `Yesterday ${time.toLowerCase()}`
  } else if (diffInDays <= 7) {
    // Within a week - show day and time
    const date = new Date(timestamp)
    const dayOfWeek = date.toLocaleDateString("en-US", { weekday: "long" })
    const time = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })

    return `${dayOfWeek} ${time.toLowerCase()}`
  } else {
    return `${diffInDays} day${diffInDays === 1 ? "" : "s"} ago`
  }
}

const messageGroups: (Omit<Message, "sentAt"> & { sentAt: number })[][] = [
  [
    {
      id: "msg-0",
      sentAt: new Date(2025, 6, 31, 11, 39).getTime(),
      user: {
        name: "Joshua Wilson",
        avatarUrl:
          "https://www.untitledui.com/images/avatars/joshua-wilson?fm=webp&q=80",
        status: "online",
      },
      text: "Thanks Olivia! Almost there. I'll work on making those changes you suggested and will shoot it over.",
    },
    {
      id: "msg-1",
      sentAt: new Date(2025, 6, 31, 11, 40).getTime(),
      user: {
        name: "Joshua Wilson",
        avatarUrl:
          "https://www.untitledui.com/images/avatars/joshua-wilson?fm=webp&q=80",
        status: "online",
      },
      text: "Hey Olivia, I've finished with the requirements doc! I made some notes in the gdoc as well for Phoenix to look over.",
    },
    {
      id: "msg-1.2",
      sentAt: new Date(2025, 6, 31, 11, 40).getTime(),
      user: {
        name: "Joshua Wilson",
        avatarUrl:
          "https://www.untitledui.com/images/avatars/joshua-wilson?fm=webp&q=80",
        status: "online",
      },
      attachment: {
        type: "pdf",
        name: "Tech requirements.pdf",
        size: "1.2 MB",
      },
    },
    {
      id: "msg-2",
      sentAt: new Date(2025, 6, 31, 11, 41).getTime(),
      user: {
        me: true,
      },
      text: "Awesome! Thanks. I'll look at this today.",
    },
    {
      id: "msg-3",
      sentAt: new Date(2025, 6, 31, 11, 44).getTime(),
      user: {
        name: "Joshua Wilson",
        avatarUrl:
          "https://www.untitledui.com/images/avatars/joshua-wilson?fm=webp&q=80",
        status: "online",
      },
      text: "No rush though—we still have to wait for Lana's designs.",
      reactions: [
        { content: "❤️‍🔥", count: 1 },
        { content: "👌", count: 1 },
      ],
    },
  ],
  [
    {
      id: "msg-4",
      sentAt: new Date(2025, 6, 31, 11, 44).getTime(),
      user: {
        name: "Joshua Wilson",
        avatarUrl:
          "https://www.untitledui.com/images/avatars/joshua-wilson?fm=webp&q=80",
        status: "online",
      },
      text: "Hey Olivia, can you please review the latest design when you can?",
    },
    {
      id: "msg-5",
      sentAt: Date.now() - 30 * 1000,
      user: {
        me: true,
      },
      text: "Sure thing, I'll have a look today. They're looking great!",
    },
  ],
]

export default function Pangram() {
  return (
    <>
      <Head title="Pangram - Theme Tester" />
      <div className="bg-secondary_subtle flex flex-col max-lg:h-dvh">
        <HeaderNavigationBase
          items={[
            { label: "Home", href: "/" },
            { label: "Dashboard", href: "/dashboard" },
            { label: "Projects", href: "/projects" },
            { label: "Tasks", href: "/tasks" },
            { label: "Reporting", href: "/reporting" },
            { label: "Messages", href: "/messages" },
          ]}
        />
        <main className="max-w-container mx-auto flex w-full flex-1 flex-col gap-x-8 max-lg:max-h-full max-lg:min-h-0 lg:flex-row lg:items-start lg:px-8 lg:pt-12 lg:pb-24">
          <div className="bg-primary ring-secondary relative flex flex-1 flex-col overflow-hidden lg:rounded-[20px] lg:shadow-xs lg:ring-1">
            <div className="after:border-secondary relative hidden flex-col gap-y-3 px-6 pt-6 pb-5 after:pointer-events-none after:absolute after:inset-0 after:border-b lg:flex">
              <div className="inline-flex">
                <Button size="md" color="link-color" iconLeading={ArrowLeft}>
                  All messages
                </Button>
              </div>
              <div className="flex items-center justify-between gap-x-4">
                <p className="text-primary text-lg font-semibold">
                  Conversation with Joshua
                </p>
                <div className="flex gap-x-3">
                  <Button
                    size="md"
                    color="secondary"
                    iconLeading={PhoneCall01}
                  />
                  <Button
                    size="md"
                    color="secondary"
                    iconLeading={VideoRecorder}
                  />
                  <Button size="md" color="secondary" iconLeading={SearchLg} />
                </div>
              </div>
            </div>
            <div className="after:border-secondary relative block pb-6 after:pointer-events-none after:absolute after:inset-0 after:border-b lg:hidden">
              <div className="px-2 pt-2">
                <img
                  src="https://www.untitledui.com/application/clouds.webp"
                  alt="Clouds"
                  className="h-36 w-full rounded-xl object-cover"
                />
              </div>
              <div className="-mt-12 flex flex-col gap-4 px-6 lg:px-6">
                <div className="flex flex-col gap-4">
                  <div className="flex items-end justify-between">
                    <AvatarProfilePhoto
                      src="https://www.untitledui.com/images/avatars/joshua-wilson?fm=webp&q=80"
                      alt="Joshua Wilson"
                      size="md"
                    />
                    <div className="flex gap-3">
                      <Button
                        size="sm"
                        color="secondary"
                        iconLeading={PhoneCall01}
                      />
                      <Button
                        size="sm"
                        color="secondary"
                        iconLeading={VideoRecorder}
                      />
                    </div>
                  </div>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <span className="text-primary text-lg font-semibold">
                          Joshua Wilson
                        </span>
                        <VerifiedTick size="lg" />
                      </div>
                      <span className="text-tertiary text-sm">
                        josh@untitledui.com
                      </span>
                    </div>
                    <TableRowActionsDropdown />
                  </div>
                </div>
                <div className="hidden gap-3 lg:flex">
                  <Button size="md" color="secondary">
                    Add to project
                  </Button>
                  <Button size="md">View projects</Button>
                </div>
              </div>
            </div>

            {/* Chat */}
            <div className="overflow-hidden lg:max-h-240 lg:min-h-240">
              <ul className="bg-primary flex h-full flex-1 flex-col gap-y-4 overflow-y-auto px-4 py-8 *:first:mt-auto lg:px-6">
                {messageGroups.map((messages, index) => (
                  <Fragment key={index}>
                    {index !== 0 && (
                      <ContentDivider type="single-line" className="py-2">
                        <span className="text-tertiary text-sm font-medium">
                          Today
                        </span>
                      </ContentDivider>
                    )}
                    {messages.map((msg, index) => (
                      <MessageItem
                        key={index}
                        msg={{ ...msg, sentAt: formatRelativeTime(msg.sentAt) }}
                        className="max-w-78 lg:max-w-150"
                      />
                    ))}
                  </Fragment>
                ))}

                {/* Typing status indicator */}
                <li className="flex gap-3">
                  <Avatar
                    src="https://www.untitledui.com/images/avatars/katherine-moss?fm=webp&q=80"
                    alt="Katherine Moss"
                    size="md"
                    status="online"
                  />
                  <div>
                    <p className="text-secondary mb-1.5 text-sm font-medium">
                      Joshua Wilson
                    </p>
                    <div className="bg-secondary text-md text-primary ring-secondary inline-flex gap-1 rounded-lg rounded-tl-none p-2.5 ring-1 ring-inset">
                      <div className="bg-fg-tertiary size-1 animate-bounce rounded-full [animation-delay:-0.3s]"></div>
                      <div className="bg-fg-quaternary size-1 animate-bounce rounded-full [animation-delay:-0.15s]"></div>
                      <div className="bg-fg-tertiary size-1 animate-bounce rounded-full"></div>
                    </div>
                  </div>
                </li>
              </ul>
            </div>

            <div className="border-secondary sticky bottom-0 border-t p-4 lg:border-none lg:p-6 lg:pt-0">
              <MessageActionAdvanced
                className="max-lg:hidden"
                onSubmit={(message) => {
                  console.log("Message:", message)
                }}
              />
              <MessageActionMinimal
                className="w-full lg:hidden"
                onSubmit={(message) => {
                  console.log("Message:", message)
                }}
              />
            </div>
          </div>

          <div className="bg-primary ring-secondary hidden w-90 flex-col gap-6 overflow-y-auto rounded-[20px] pb-6 shadow-xs ring-1 ring-inset lg:flex">
            {/* Heading */}
            <div className="pb-6">
              <div className="px-2 lg:pt-2">
                <img
                  src="https://www.untitledui.com/application/clouds.webp"
                  alt="Clouds"
                  className="h-22 w-full rounded-xl object-cover lg:h-36"
                />
              </div>
              <div className="-mt-12 flex flex-col gap-4 px-4 lg:px-6">
                <div className="flex flex-col gap-4">
                  <div className="flex items-end justify-between">
                    <AvatarProfilePhoto
                      src="https://www.untitledui.com/images/avatars/joshua-wilson?fm=webp&q=80"
                      alt="Joshua Wilson"
                      size="md"
                    />
                    <div className="flex gap-3">
                      <Button
                        size="sm"
                        color="secondary"
                        iconLeading={PhoneCall01}
                      />
                      <Button
                        size="sm"
                        color="secondary"
                        iconLeading={VideoRecorder}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col items-start gap-4">
                    <div className="max-w-50 min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className="text-primary truncate text-lg font-semibold">
                          Joshua Wilson
                        </p>
                        <VerifiedTick size="lg" />
                      </div>
                      <p className="text-tertiary truncate text-sm">
                        josh@untitledui.com
                      </p>
                    </div>
                    <dl className="flex items-center gap-4">
                      <div className="flex flex-col gap-0.5">
                        <dt className="text-quaternary text-xs font-medium">
                          Followers
                        </dt>
                        <dd className="text-md text-primary font-semibold">
                          32,086
                        </dd>
                      </div>

                      <hr className="bg-border-primary h-11 w-px rounded-full border-none" />

                      <div className="flex flex-col gap-0.5">
                        <dt className="text-quaternary text-xs font-medium">
                          Following
                        </dt>
                        <dd className="text-md text-primary font-semibold">
                          4,698
                        </dd>
                      </div>

                      <hr className="bg-border-primary h-11 w-px rounded-full border-none" />

                      <div className="flex flex-col gap-0.5">
                        <dt className="text-quaternary text-xs font-medium">
                          Posts
                        </dt>
                        <dd className="text-md text-primary font-semibold">
                          128
                        </dd>
                      </div>

                      <hr className="bg-border-primary h-11 w-px rounded-full border-none" />

                      <div className="flex flex-col gap-0.5">
                        <dt className="text-quaternary text-xs font-medium">
                          Collections
                        </dt>
                        <dd className="text-md text-primary font-semibold">
                          24
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>
                <div className="hidden gap-3 lg:flex">
                  <Button size="md" color="secondary" iconLeading={Send01}>
                    Message
                  </Button>
                  <Button size="md">View projects</Button>
                </div>
              </div>
            </div>
            <div className="hidden flex-col gap-6 px-6 lg:flex">
              <div className="flex flex-col">
                <p className="text-primary text-sm font-semibold">About</p>
                <p className="text-tertiary mt-1 text-sm">
                  I&apos;m a Designer based in Melbourne. I co-founded{" "}
                  <a
                    href="#"
                    className="outline-focus-ring rounded-xs underline underline-offset-3 focus-visible:outline-2 focus-visible:outline-offset-2"
                  >
                    Layers Studio™
                  </a>{" "}
                  where we help early stage founders and startups take their
                  product from 0→1.
                </p>
                <ul className="mt-4 flex flex-col gap-2">
                  <li className="text-tertiary flex gap-2 text-sm">
                    <MarkerPin02 className="text-fg-quaternary size-5" />
                    Melbourne, Australia
                  </li>
                  <li className="text-tertiary flex gap-2 text-sm">
                    <Link01 className="text-fg-quaternary size-5" />
                    layers.studio
                  </li>
                </ul>
              </div>
              <div className="flex flex-col gap-4">
                <p className="text-primary text-sm font-semibold">
                  Work experience
                </p>
                <span className="flex w-full flex-row items-start gap-3">
                  <Avatar
                    src="https://www.untitledui.com/logos/images/Layers.jpg"
                    size="lg"
                    contrastBorder
                  />
                  <span className="flex flex-col gap-2">
                    <span className="flex flex-col">
                      <p className="text-secondary text-sm font-semibold">
                        Founder
                      </p>
                      <p className="text-tertiary text-sm">Layers Studio™</p>
                    </span>
                    <p className="text-tertiary text-sm">May 2020 – Present</p>
                  </span>
                </span>
                <span className="flex w-full flex-row items-start gap-3">
                  <Avatar
                    src="https://www.untitledui.com/logos/images/Sisyphus.jpg"
                    size="lg"
                    contrastBorder
                  />
                  <span className="flex flex-col gap-2">
                    <span className="flex flex-col">
                      <p className="text-secondary text-sm font-semibold">
                        UX Designer
                      </p>
                      <p className="text-tertiary text-sm">Sisyphus</p>
                    </span>
                    <p className="text-tertiary text-sm">Jan 2018 – May 2020</p>
                  </span>
                </span>
                <span className="flex w-full flex-row items-start gap-3">
                  <Avatar
                    src="https://www.untitledui.com/logos/images/Catalog.jpg"
                    size="lg"
                    contrastBorder
                  />
                  <span className="flex flex-col gap-2">
                    <span className="flex flex-col">
                      <p className="text-secondary text-sm font-semibold">
                        Visual Designer
                      </p>
                      <p className="text-tertiary text-sm">Catalog</p>
                    </span>
                    <p className="text-tertiary text-sm">Mar 2017 – Jan 2018</p>
                  </span>
                </span>
              </div>
            </div>
          </div>
        </main>

        {/* Component Showcase Section */}
        <div className="bg-primary w-full space-y-8 px-4 py-12 lg:px-8">
          <div className="mx-auto max-w-4xl space-y-12">
            {/* Section Header */}
            <div className="space-y-2">
              <h2 className="text-display-xs text-primary font-semibold">
                Component Showcase
              </h2>
              <p className="text-md text-tertiary">
                Testing utility colors, state tokens, and interactive components
              </p>
            </div>

            {/* Badges Section */}
            <div className="space-y-4">
              <h3 className="text-primary text-lg font-semibold">Badges</h3>
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge type="pill-color" color="gray">
                    Gray
                  </Badge>
                  <Badge type="pill-color" color="brand">
                    Brand
                  </Badge>
                  <Badge type="pill-color" color="error">
                    Error
                  </Badge>
                  <Badge type="pill-color" color="warning">
                    Warning
                  </Badge>
                  <Badge type="pill-color" color="success">
                    Success
                  </Badge>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge type="color" color="blue">
                    Blue
                  </Badge>
                  <Badge type="color" color="indigo">
                    Indigo
                  </Badge>
                  <Badge type="color" color="purple">
                    Purple
                  </Badge>
                  <Badge type="color" color="pink">
                    Pink
                  </Badge>
                  <Badge type="color" color="orange">
                    Orange
                  </Badge>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge type="modern" size="sm">
                    Modern Small
                  </Badge>
                  <Badge type="modern" size="md">
                    Modern Medium
                  </Badge>
                  <Badge type="modern" size="lg">
                    Modern Large
                  </Badge>
                </div>
              </div>
            </div>

            {/* Inputs Section */}
            <div className="space-y-4">
              <h3 className="text-primary text-lg font-semibold">
                Input Fields
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label="Email"
                  placeholder="olivia@untitledui.com"
                  size="sm"
                />
                <Input
                  label="Password"
                  type="password"
                  placeholder="••••••••"
                  size="sm"
                />
                <Input
                  label="Disabled"
                  placeholder="Cannot edit"
                  isDisabled
                  size="sm"
                />
                <Input
                  label="With error"
                  placeholder="invalid@email"
                  isInvalid
                  hint="Please enter a valid email"
                  size="sm"
                />
              </div>
            </div>

            {/* Checkboxes Section */}
            <div className="space-y-4">
              <h3 className="text-primary text-lg font-semibold">Checkboxes</h3>
              <div className="space-y-3">
                <Checkbox
                  label="Remember me"
                  hint="Save my login details for next time"
                />
                <Checkbox defaultSelected label="Selected by default" />
                <Checkbox isDisabled label="Disabled checkbox" />
                <Checkbox
                  isDisabled
                  defaultSelected
                  label="Disabled and selected"
                />
              </div>
            </div>

            {/* Toggles Section */}
            <div className="space-y-4">
              <h3 className="text-primary text-lg font-semibold">Toggles</h3>
              <div className="space-y-3">
                <Toggle
                  label="Enable notifications"
                  hint="Get notified when someone mentions you"
                />
                <Toggle
                  defaultSelected
                  label="Auto-save"
                  hint="Automatically save changes as you type"
                />
                <Toggle
                  slim
                  label="Slim variant"
                  hint="Compact toggle design"
                />
                <Toggle size="md" label="Medium size" />
                <Toggle isDisabled label="Disabled toggle" />
              </div>
            </div>

            {/* Radio Buttons Section */}
            <div className="space-y-4">
              <h3 className="text-primary text-lg font-semibold">
                Radio Buttons
              </h3>
              <RadioGroup aria-label="Choose a plan" defaultValue="pro">
                <RadioButton
                  value="free"
                  label="Free"
                  hint="For personal projects and testing"
                />
                <RadioButton
                  value="pro"
                  label="Pro"
                  hint="For professional work and teams"
                />
                <RadioButton
                  value="enterprise"
                  label="Enterprise"
                  hint="For large organizations"
                />
              </RadioGroup>
            </div>

            {/* Progress Bars Section */}
            <div className="space-y-4">
              <h3 className="text-primary text-lg font-semibold">
                Progress Indicators
              </h3>
              <div className="space-y-3">
                <div className="space-y-2">
                  <span className="text-tertiary text-sm">Upload progress</span>
                  <ProgressBar value={45} labelPosition="right" />
                </div>
                <div className="space-y-2">
                  <span className="text-tertiary text-sm">Processing</span>
                  <ProgressBar value={75} labelPosition="right" />
                </div>
                <div className="space-y-2">
                  <span className="text-tertiary text-sm">Complete</span>
                  <ProgressBar value={100} labelPosition="right" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
